import React, { useState, useEffect } from 'react';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { ref, query, orderByChild, equalTo, get } from 'firebase/database';
import { auth, database } from '../firebase/config';
import {
  Card,
  Button,
  Input,
  FormGroup,
  Label,
  ErrorText,
  Title,
  Subtitle,
  Container
} from '../styles/GlobalStyles';
import styled from 'styled-components';
import connectingFoodLogo from '../assets/LogoCF_Branco.png';
import { validateCNPJ, formatCNPJ as formatCNPJUtil } from '../utils/cnpjValidator';
import { useUserAuth } from '../contexts/UserAuthContext';

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const CenteredForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
`;

const CenteredFormGroup = styled(FormGroup)`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  ${Input} {
    max-width: 350px;
  }
`;

const CenteredButton = styled(Button)`
  width: 100%;
  max-width: 350px;
  margin: 16px 0;
`;

const LogoImage = styled.img`
  width: 350px;
  height: auto;
  margin-bottom: 24px;
  display: block;
  margin-left: auto;
  margin-right: auto;
`;

const LoadingIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 16px 0;
  color: #0d6efd;
  font-size: 14px;
  
  &::before {
    content: '';
    width: 16px;
    height: 16px;
    border: 2px solid #e3f2fd;
    border-top: 2px solid #0d6efd;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;



interface LoginProps {
  onSwitchToRegister: (cnpj?: string) => void;
  prefilledCnpj?: string;
  onCnpjUsed?: () => void;
}

const Login: React.FC<LoginProps> = ({ onSwitchToRegister, prefilledCnpj, onCnpjUsed }) => {
  const [cnpj, setCnpj] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [cnpjExists, setCnpjExists] = useState(false);
  const [cnpjError, setCnpjError] = useState('');
  const [cnpjLoading, setCnpjLoading] = useState(false);
  const { login } = useUserAuth();

  // Preencher CNPJ automaticamente quando fornecido
  useEffect(() => {
    if (prefilledCnpj) {
      setCnpj(prefilledCnpj);
      if (onCnpjUsed) {
        onCnpjUsed();
      }
    }
  }, [prefilledCnpj, onCnpjUsed]);



  const handleCNPJChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCNPJUtil(e.target.value);
    setCnpj(formatted);
    setCnpjError('');
    setError('');
    
    // Verificar se CNPJ existe quando tiver 14 dígitos
    const cleanCNPJ = formatted.replace(/\D/g, '');
    if (cleanCNPJ.length === 14) {
      // Validar CNPJ antes de prosseguir
      if (!validateCNPJ(formatted)) {
        setCnpjError('CNPJ inválido. Verifique os dígitos digitados.');
        setShowPassword(false);
        setCnpjExists(false);
        setCnpjLoading(false);
        return;
      }
      
      setCnpjLoading(true);
      const email = await findEmailByCNPJ(formatted);
      setCnpjLoading(false);
      
      if (email) {
        setShowPassword(true);
        setCnpjExists(true);
        setError('');
      } else {
        setShowPassword(false);
        setCnpjExists(false);
      }
    } else {
      setShowPassword(false);
      setCnpjExists(false);
      setCnpjLoading(false);
    }
  };

  const findEmailByCNPJ = async (cnpjValue: string): Promise<string | null> => {
    try {
      const cleanCNPJ = cnpjValue.replace(/\D/g, '');
      const formattedCNPJ = formatCNPJUtil(cleanCNPJ);
      
      const oscsRef = ref(database, 'oscs');
      
      // Primeira tentativa: buscar com CNPJ formatado
      const formattedQuery = query(oscsRef, orderByChild('cnpj'), equalTo(formattedCNPJ));
      const formattedSnapshot = await get(formattedQuery);
      
      if (formattedSnapshot.exists()) {
        const data = formattedSnapshot.val();
        const oscData = Object.values(data)[0] as any;
        return oscData.email;
      }
      
      // Segunda tentativa: buscar com CNPJ limpo (apenas números)
      const cleanQuery = query(oscsRef, orderByChild('cnpj'), equalTo(cleanCNPJ));
      const cleanSnapshot = await get(cleanQuery);
      
      if (cleanSnapshot.exists()) {
        const data = cleanSnapshot.val();
        const oscData = Object.values(data)[0] as any;
        return oscData.email;
      }
      
      // Terceira tentativa: buscar manualmente em todos os registros
      // (para casos onde há inconsistências na formatação)
      const allSnapshot = await get(oscsRef);
      if (allSnapshot.exists()) {
        const allData = allSnapshot.val();
        
        for (const oscData of Object.values(allData)) {
          const osc = oscData as any;
          if (osc.cnpj) {
            const oscCleanCNPJ = osc.cnpj.replace(/\D/g, '');
            if (oscCleanCNPJ === cleanCNPJ) {
              return osc.email;
            }
          }
        }
      }
      
      return null;
    } catch (error) {
      
      return null;
    }
  };

  const getLoginErrorMessage = (errorCode: string): string => {
    switch (errorCode) {
      case 'auth/user-not-found':
        return '❌ Conta não encontrada. Verifique se sua organização está cadastrada';
      case 'auth/wrong-password':
        return '❌ Senha incorreta. Verifique sua senha ou use "Esqueci minha senha"';
      case 'auth/invalid-email':
        return '❌ Email inválido associado ao CNPJ';
      case 'auth/user-disabled':
        return '❌ Esta conta foi desativada. Entre em contato com o suporte';
      case 'auth/too-many-requests':
        return '⏰ Muitas tentativas de login. Aguarde alguns minutos antes de tentar novamente';
      case 'auth/network-request-failed':
        return '🌐 Erro de conexão. Verifique sua internet e tente novamente';
      case 'auth/invalid-credential':
        return '❌ Credenciais inválidas. Verifique seu CNPJ e senha';
      case 'auth/account-exists-with-different-credential':
        return '❌ Já existe uma conta com este email usando outro método de login';
      case 'auth/weak-password':
        return '❌ Senha muito fraca. Use pelo menos 6 caracteres';
      case 'auth/email-already-in-use':
        return '❌ Este email já está sendo usado por outra conta';
      case 'auth/operation-not-allowed':
        return '❌ Operação não permitida. Entre em contato com o suporte';
      case 'auth/requires-recent-login':
        return '🔐 Por segurança, faça login novamente para continuar';
      default:
        return '❌ Erro inesperado ao fazer login. Tente novamente em alguns minutos';
    }
  };

  const getPasswordResetErrorMessage = (errorCode: string): string => {
    switch (errorCode) {
      case 'auth/user-not-found':
        return 'Não foi possível encontrar uma conta associada a este CNPJ';
      case 'auth/invalid-email':
        return 'Email inválido associado ao CNPJ';
      case 'auth/too-many-requests':
        return 'Muitas tentativas de recuperação. Aguarde alguns minutos antes de tentar novamente';
      case 'auth/network-request-failed':
        return 'Erro de conexão. Verifique sua internet e tente novamente';
      default:
        return 'Erro ao enviar email de recuperação. Tente novamente em alguns minutos';
    }
  };

  const handleForgotPassword = async () => {
    if (!cnpj.trim()) {
      setError('Por favor, digite o CNPJ para recuperar a senha');
      return;
    }

    setResetLoading(true);
    setError('');
    setResetMessage('');

    try {
      const email = await findEmailByCNPJ(cnpj);
      
      if (!email) {
        setError('CNPJ não encontrado no sistema. Verifique se sua organização está cadastrada');
        return;
      }

      await sendPasswordResetEmail(auth, email);
      setResetMessage('✅ Email de recuperação enviado com sucesso! Verifique sua caixa de entrada e spam.');
    } catch (error: any) {
      const errorMessage = getPasswordResetErrorMessage(error.code);
      setError(errorMessage);
    } finally {
      setResetLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResetMessage('');
    setCnpjError('');
    setLoading(true);

    try {
      // Verificar se CNPJ tem 14 dígitos
      const cleanCNPJ = cnpj.replace(/\D/g, '');
      if (cleanCNPJ.length !== 14) {
        setCnpjError('Por favor, digite um CNPJ válido com 14 dígitos');
        setLoading(false);
        return;
      }
      
      // Validar CNPJ
      if (!validateCNPJ(cnpj)) {
        setCnpjError('CNPJ inválido. Verifique os dígitos digitados.');
        setLoading(false);
        return;
      }

      // Buscar email associado ao CNPJ
      const email = await findEmailByCNPJ(cnpj);
      
      if (!email) {
        // CNPJ não cadastrado - direcionar para cadastro
        onSwitchToRegister(cnpj);
        setLoading(false);
        return;
      }

      // CNPJ cadastrado - fazer login
      if (!password) {
        setError('Por favor, digite sua senha');
        setLoading(false);
        return;
      }

      // Fazer login usando o contexto de usuário
      await login(email, password);
      // Usuário será redirecionado automaticamente pelo UserAuthContext
    } catch (error: any) {
      const errorMessage = getLoginErrorMessage(error.code);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <Container>
        <Card>
          <LogoImage src={connectingFoodLogo} alt="Connecting Food" />
          <Title>Banco de Parcerias</Title>
          <Subtitle>
            Se você tem interesse em fazer parte do nosso banco de possíveis parcerias, acesse o sistema e preencha o formulário com as informações da sua Organização.
            <br />
            O preenchimento do formulário é apenas a primeira etapa. Ele não garante o recebimento imediato de doações ou a parceria com um doador.
            <br />
            Se sua Organização já é parceira da Connecting Food, use este formulário para atualizar os dados cadastrais e manter a parceria ativa.
          </Subtitle>
          
          <CenteredForm onSubmit={handleSubmit}>
            <CenteredFormGroup>
              <Label>Digite o CNPJ da OSC</Label>
              <Input
                type="text"
                value={cnpj}
                onChange={handleCNPJChange}
                placeholder="00.000.000/0000-00"
                maxLength={18}
                required
                style={cnpjError ? { borderColor: '#dc3545' } : {}}
              />
              {cnpjError && <div style={{ color: '#dc3545', fontSize: '14px', marginTop: '8px' }}>{cnpjError}</div>}
            </CenteredFormGroup>

            {cnpjLoading && (
              <LoadingIndicator>
                Verificando CNPJ...
              </LoadingIndicator>
            )}

            {showPassword && (
              <CenteredFormGroup>
                <Label>Senha</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Sua senha"
                  required={cnpjExists}
                />
              </CenteredFormGroup>
            )}

            {error && <ErrorText>{error}</ErrorText>}
            {resetMessage && <div style={{ color: '#28a745', fontSize: '14px', marginBottom: '16px', textAlign: 'center' }}>{resetMessage}</div>}

            <CenteredButton type="submit" disabled={loading}>
              {loading ? (cnpjExists ? 'Entrando...' : 'Verificando...') : 'Entrar/Cadastrar'}
            </CenteredButton>
            
            {showPassword && (
              <div style={{ textAlign: 'center', marginTop: '16px' }}>
                <button 
                  type="button" 
                  onClick={handleForgotPassword}
                  disabled={resetLoading}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#667eea',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontSize: '14px'
                  }}
                >
                  {resetLoading ? 'Enviando...' : 'Esqueci minha senha'}
                </button>
              </div>
            )}
          </CenteredForm>
          

        </Card>
      </Container>
    </LoginContainer>
  );
};

export default Login;